import classNames from 'classnames';
import React, { useMemo, useState, JSX } from 'react';
import {
	CarouselImageFormat,
	CarouselVariant,
	IBaseListAction,
	ICarouselItem,
} from '@enuan/chat__utils';
import { Button, IButtonProps } from '../../../UI';
import { useParseText } from '../../../../hooks/useParseText';
import { useBreakpoint } from '../../../../hooks/useBreakpoint';
import { styled } from '@mui/material/styles';

export type Props = Omit<JSX.IntrinsicElements['div'], 'children'> & {
	item: ICarouselItem;
	variant?: CarouselVariant;
	image_format?: CarouselImageFormat;
	onAction?: (action: IBaseListAction) => void;
};

export const CarouselItem = styled(
	({
		className,
		item: { image, title, description, actions },
		variant = CarouselVariant.RICH,
		image_format = CarouselImageFormat.WIDE,
		onAction,
		...props
	}: Props) => {
		const actionsLines = useMemo(() => {
			if (variant === CarouselVariant.SIMPLE) {
				return [Array.isArray(actions[0]) ? actions[0] : [actions[0]]];
			} else {
				return actions
					.slice(0, 2)
					.map((line) => (Array.isArray(line) ? line : [line]));
			}
		}, [variant, actions]);

		const { isMobile } = useBreakpoint();
		const isSquare = image_format === CarouselImageFormat.SQUARE;

		const actionsLinesCount = actionsLines.length || 1;

		const { html: description_html, text: description_text } =
			useParseText(description);
		const [descriptionBreaklines, setDescriptionBreaklines] =
			useState<number>();

		return (
			<div className={classNames(className, 'carousel__item')} {...props}>
				<div className="carousel__image">
					<img src={image} alt={title ?? ''} />
				</div>

				<div className="carousel__content">
					<CarouselTitle
						title={title}
						actionsLinesCount={actionsLinesCount}
						isSquare={isSquare}
						isMobile={!!isMobile}
						onBreaklinesChange={setDescriptionBreaklines}
					/>
					{description_html && (
						<div
							className="carousel__description"
							dangerouslySetInnerHTML={{ __html: description_html }}
							title={description_text}
							style={
								descriptionBreaklines
									? { WebkitLineClamp: descriptionBreaklines }
									: undefined
							}
						/>
					)}
				</div>

				{actionsLines.map((actionsLine, lineIndex) => (
					<div
						key={lineIndex}
						className={classNames(
							'carousel__actions',
							'carousel__actions--inline',
						)}
					>
						{actionsLine.map((action, actionIndex) => (
							<Button
								key={actionIndex}
								onClick={() => onAction?.(action)}
								className={classNames('carousel__action', action.className)}
								color={action.color as IButtonProps['color']}
							>
								{action.label}
							</Button>
						))}
					</div>
				))}

				{variant === CarouselVariant.SIMPLE && (
					<div className="carousel__overlay" />
				)}
			</div>
		);
	},
	{ label: 'CarouselItem', target: 'CarouselItem-root' },
)({
	'&.carousel__item': {
		height: '30em',
		overflow: 'hidden',
		borderRadius: '0 7px 7px 7px',
		display: 'flex !important',
		flexDirection: 'column',
		position: 'relative',
		backgroundColor: 'var(--carousel_background)',
	},
	'& .carousel__image img': {
		width: '100%',
	},
	'& .carousel__actions': {
		position: 'relative',
		'& .button': {
			fontSize: '1.3em',
			width: '100%',
			borderRadius: 0,
			padding: '6px',
			fontWeight: 'bold',
		},
		'&.carousel__actions--inline': {
			display: 'flex',
			'& .button': {
				flex: 1,
			},
		},
		'&:last-child, &:last-child .button:last-child': {
			borderBottomRightRadius: 'inherit',
			borderBottomLeftRadius: 'inherit',
		},
	},
	'.carousel--keep & .carousel__image': {
		maxHeight: '50%',
	},
	'.carousel--wide & .carousel__image': {
		height: '14em',
	},
	'.carousel--with-margins & .carousel__image': {
		marginTop: '0.5em',
		marginLeft: '0.5em',
		marginRight: '0.5em',
	},
	'.carousel--cover & .carousel__image img': {
		objectFit: 'cover',
		height: '100%',
	},
	'.carousel--rich &': {
		'& .carousel__overlay': {
			display: 'none',
		},
		'& .carousel__image': {
			maxWidth: '100%',
			height: '50%',
			'& img': {
				height: '100%',
				objectFit: 'contain',
			},
		},
		'& .carousel__content': {
			padding: '12px',
			flex: 1,
			overflow: 'hidden',
		},
		'& .carousel__title': {
			fontWeight: 'bold',
			fontSize: '1.5em',
			color: 'var(--carousel__title)',
			display: '-webkit-box',
			WebkitLineClamp: 3,
			WebkitBoxOrient: 'vertical',
			overflow: 'hidden',
		},
		'& .carousel__content p:first-child, & .carousel__title p:first-child': {
			marginTop: 0,
		},
		'& .carousel__content p:last-child, & .carousel__title p:last-child': {
			marginBottom: 0,
		},
		'& .carousel__description': {
			fontSize: '1.1em',
			fontFamily: 'Roboto, sans-serif',
			fontStyle: 'normal',
			fontWeight: 400,
			color: 'var(--carousel__description)',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			display: '-webkit-box',
			WebkitBoxOrient: 'vertical',
			'& a': {
				textDecoration: 'underline',
				color: 'var(--carousel__description__link_color)',
			},
		},
		'& .carousel__actions .button': {
			transition: 'background 0.3s',
			color: 'var(--carousel__action_color)',
			background: 'var(--carousel__action_background)',
			'&:nth-child(even)': {
				'.carousel__actions:not(.carousel__actions--inline) &': {
					borderTop: '1px solid var(--carousel__action__separator_color)',
				},
			},
			'&:hover': {
				background: 'var(--carousel__action--hover_background)',
				color: 'var(--carousel__action--hover_color)',
			},
		},
	},
	'.carousel--simple &': {
		'& .carousel__image': {
			width: '100%',
			height: '100%',
			overflow: 'hidden',
		},
		'&.carousel__item': {
			justifyContent: 'center',
			background: 'transparent',
			position: 'relative',
		},
		'& .carousel__content': {
			display: 'none',
		},
		'& .carousel__actions': {
			zIndex: 2,
			position: 'absolute',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'stretch',
			height: '100%',
			width: '100%',
			'& .button': {
				backgroundColor: 'var(--carousel--simple__action_background)',
				color: 'var(--carousel--simple__action_color)',
				borderBottomColor: 'var(--carousel--simple__action_border)',
				borderBottomWidth: '1px',
				borderBottomStyle: 'solid',
				height: '4em',
				'&:not(:nth-child(1))': {
					display: 'none',
				},
			},
		},
		'&:hover .carousel__overlay': {
			opacity: 0.3,
		},
		'& .carousel__overlay': {
			zIndex: 1,
			height: '100%',
			width: '100%',
			position: 'absolute',
			cursor: 'pointer',
			WebkitTransition: 'all 1s',
			transition: 'all 1s',
			background: 'var(--carousel--simple__overlay_background)',
			opacity: 0.1,
		},
	},
});

type CarouselTitleProps = {
	title?: string;
	actionsLinesCount: number;
	isSquare: boolean;
	isMobile: boolean;
	onBreaklinesChange: (value: number | undefined) => void;
};

const CarouselTitle = ({
	title,
	actionsLinesCount,
	isSquare,
	isMobile,
	onBreaklinesChange,
}: CarouselTitleProps) => {
	const [fontHeight, setFontHeight] = useState<number>();
	const { html, text } = useParseText(title);

	if (!html) return null;
	if (typeof fontHeight === 'undefined') {
		return (
			<span
				className="carousel__title"
				ref={(element) => {
					if (element?.offsetHeight) {
						setFontHeight(element.offsetHeight);
					}
				}}
				style={{ visibility: 'hidden' }}
			>
				&nbsp;
			</span>
		);
	}

	return (
		<span
			className="carousel__title"
			dangerouslySetInnerHTML={{ __html: html }}
			title={text}
			ref={(element) => {
				if (element) {
					const breaklines = Math.ceil(
						element.offsetHeight / (fontHeight ?? 0),
					);
					const size = getDescriptionSize(
						breaklines,
						actionsLinesCount,
						isSquare,
						isMobile,
					);
					onBreaklinesChange(size);
				}
			}}
		/>
	);
};

const getDescriptionSize = (
	breaklines: number,
	actionsCount: number,
	square: boolean,
	mobile: boolean,
) => {
	if (breaklines) {
		// Desktop
		if (!mobile && breaklines === 1 && actionsCount === 1 && !square) return 7;
		if (!mobile && breaklines === 1 && actionsCount === 1 && square) return 6;
		if (!mobile && breaklines === 1 && actionsCount === 2 && !square) return 5;
		if (!mobile && breaklines === 1 && actionsCount === 2 && square) return 4;

		if (!mobile && breaklines === 2 && actionsCount === 1 && !square) return 6;
		if (!mobile && breaklines === 2 && actionsCount === 1 && square) return 5;
		if (!mobile && breaklines === 2 && actionsCount === 2 && !square) return 4;
		if (!mobile && breaklines === 2 && actionsCount === 2 && square) return 3;

		if (!mobile && breaklines === 3 && actionsCount === 1 && !square) return 4;
		if (!mobile && breaklines === 3 && actionsCount === 1 && square) return 3;
		if (!mobile && breaklines === 3 && actionsCount === 2 && !square) return 2;
		if (!mobile && breaklines === 3 && actionsCount === 2 && square) return 1;

		// Mobile
		if (mobile && breaklines === 1 && actionsCount === 1 && !square) return 8;
		if (mobile && breaklines === 1 && actionsCount === 1 && square) return 8;
		if (mobile && breaklines === 1 && actionsCount === 2 && !square) return 8;
		if (mobile && breaklines === 1 && actionsCount === 2 && square) return 6;

		if (mobile && breaklines === 2 && actionsCount === 1 && !square) return 7;
		if (mobile && breaklines === 2 && actionsCount === 1 && square) return 6;
		if (mobile && breaklines === 2 && actionsCount === 2 && !square) return 6;
		if (mobile && breaklines === 2 && actionsCount === 2 && square) return 5;

		if (mobile && breaklines >= 3 && actionsCount === 1 && !square) return 6;
		if (mobile && breaklines >= 3 && actionsCount === 1 && square) return 5;
		if (mobile && breaklines >= 3 && actionsCount === 2 && !square) return 4;
		if (mobile && breaklines >= 3 && actionsCount === 2 && square) return 3;
	}
	return undefined;
};
